import React, { useState, useEffect, useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  Legend,
} from "recharts";
import {
  ValueType,
  NameType,
} from "recharts/types/component/DefaultTooltipContent";

interface DataItem {
  name: string;
  value: number;
  originalValue: number;
  color: string;
}

export default function PortfolioPiechart({
  chartHeight = "calc(100% - 50px)",
  data,
}: {
  chartHeight?: string;
  data: any[];
}) {
  const [activeItems, setActiveItems] = useState<{ [key: string]: boolean }>(
    {}
  );

  const configMap: { [key: string]: { color: string; displayName: string } } =
    useMemo(
      () => ({
        re_percentage: { color: "#FF8894", displayName: "Renewable Energy" },
        isgs_percentage: { color: "#639FCB", displayName: "ISGS" },
        sgu_percentage: { color: "#34656D", displayName: "SGU" },
        outage_percentage: { color: "#F1935C", displayName: "Outage" },
      }),
      []
    );

  const dataPie: DataItem[] = useMemo(() => {
    if (data.length > 0 && data[0]) {
      return Object.keys(data[0])
        .filter((key) => configMap[key]) // Only include keys that are in configMap
        .map((key) => {
          const originalValue = data[0][key] || 0;
          const absValue = Math.abs(originalValue);
          return {
            name: configMap[key]?.displayName || key.toUpperCase(),
            value: absValue === 0 ? 0.01 : absValue,
            originalValue: originalValue,
            color: configMap[key]?.color || "#CCCCCC",
          };
        });
    }
    return [];
  }, [configMap, data]);

  useEffect(() => {
    if (dataPie.length > 0) {
      const initialState: { [key: string]: boolean } = {};
      dataPie.forEach((item) => {
        initialState[item.name] = true;
      });
      setActiveItems(initialState);
    }
  }, [dataPie]);

  const displayData = useMemo(() => {
    return dataPie.filter((item) => activeItems[item.name] !== false);
  }, [dataPie, activeItems]);

  const handleLegendClick = (name: string) => {
    setActiveItems((prev) => {
      const activeCount = Object.values(prev).filter(Boolean).length;
      const allActive = activeCount === dataPie.length;
      const isCurrentActive = prev[name];

      if (allActive) {
        const newState: { [key: string]: boolean } = {};
        dataPie.forEach((item) => {
          newState[item.name] = item.name === name;
        });
        return newState;
      } else if (activeCount === 1 && isCurrentActive) {
        const newState: { [key: string]: boolean } = {};
        dataPie.forEach((item) => {
          newState[item.name] = true;
        });
        return newState;
      } else {
        return {
          ...prev,
          [name]: !prev[name],
        };
      }
    });
  };

  const CustomTooltip: React.FC<TooltipProps<ValueType, NameType>> = ({
    active,
    payload,
  }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as DataItem;
      return (
        <div
          className="rounded-lg shadow-xl p-2"
          style={{
            backgroundColor: "white",
            border: "2px solid #f3f4f6",
            borderRadius: "12px",
          }}
        >
          <div className="flex items-center gap-2 mb-0">
            <span
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: data.color }}
            />
            <span className="font-medium text-xs" style={{ color: data.color }}>
              {payload[0].name}
            </span>
          </div>
          <div className="text-[#34656D] text-xs">
            {data.originalValue.toLocaleString()} %
          </div>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = (props: any) => {
    const RADIAN = Math.PI / 180;
    const { cx, cy, midAngle, outerRadius, name, percent } = props;

    if (percent < 0.03) return null;

    const radius = outerRadius + 35;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    let y = cy + radius * Math.sin(-midAngle * RADIAN);
    const isRightSide = x > cx;

    // Calculate all label positions first to detect overlaps
    const allLabels = displayData.map((item, idx) => {
      const total = displayData.reduce((sum, d) => sum + d.value, 0);
      let angle = 0;
      for (let i = 0; i < idx; i++) {
        angle += (displayData[i].value / total) * 360;
      }
      angle += ((item.value / total) * 360) / 2;

      const lx = cx + radius * Math.cos(-angle * RADIAN);
      const ly = cy + radius * Math.sin(-angle * RADIAN);

      return {
        name: item.name,
        x: lx,
        y: ly,
        isRight: lx > cx,
        angle: angle,
      };
    });

    // Find current label
    const currentLabel = allLabels.find((l) => l.name === name);
    if (!currentLabel) return null;

    // Check for overlaps with other labels on the same side
    const sameSideLabels = allLabels.filter(
      (l) => l.name !== name && l.isRight === currentLabel.isRight
    );

    const minGap = 20;
    for (const other of sameSideLabels) {
      const distance = Math.abs(currentLabel.y - other.y);
      if (distance < minGap) {
        // Adjust position
        if (currentLabel.y > other.y) {
          y = other.y + minGap;
        } else {
          y = other.y - minGap;
        }
      }
    }

    return (
      <text
        x={x}
        y={y}
        fill="#666"
        textAnchor={isRightSide ? "start" : "end"}
        dominantBaseline="central"
        style={{ fontSize: "11px", fontWeight: 500 }}
      >
        {name}
      </text>
    );
  };

  const CustomLegend = () => {
    return (
      <div className="flex flex-col justify-center items-start gap-3">
        {dataPie.map((entry, index) => {
          const isActive = activeItems[entry.name] !== false;
          return (
            <button
              key={`legend-${index}`}
              onClick={() => handleLegendClick(entry.name)}
              className="line-legend flex items-center gap-2 cursor-pointer transition-all p-1 bg-gray-100 rounded hover:opacity-80"
              style={{
                opacity: isActive ? 1 : 0.4,
              }}
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0 transition-all"
                style={{
                  backgroundColor: entry.color,
                  opacity: isActive ? 1 : 0.5,
                }}
              />
              <span
                className="text-[10px] font-medium transition-all"
                style={{
                  color: isActive ? entry.color : "#9CA3AF",
                }}
              >
                {entry.name}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={displayData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomLabel}
            // innerRadius={60}
            outerRadius={85}
            fill="#8884d8"
            dataKey="value"
            strokeWidth={2}
            stroke="#fff"
            filter="drop-shadow(5px 8px 8px rgba(0,0,0,0.15))"
            paddingAngle={2}
          >
            {displayData.map((entry: DataItem, index: number) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            content={<CustomLegend />}
            verticalAlign="middle"
            align="right"
            layout="vertical"
          />
        </PieChart>
      </ResponsiveContainer>
    </>
  );
}
